import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { apiFetch } from '../apiClient';

interface User {
  id: number;
  username: string;
  email?: string;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login?: string;
  jevis_user_id?: string;
  profile_picture_url?: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: () => void | Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve essere usato dentro AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const JEVIS_AUTH_URL =
  (import.meta.env.VITE_JEVIS_AUTH_URL as string | undefined)?.replace(/\/$/, '') ||
  'https://api.jevis.it';

const JEVIS_CALLBACK_URL =
  (import.meta.env.VITE_JEVIS_CALLBACK_URL as string | undefined) ||
  `${window.location.origin}/api/auth/callback`;

const REFRESH_INTERVAL_MS = 12 * 60 * 1000; // 12 min (< 15 min token kernel)

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const response = await apiFetch('auth/refresh', { method: 'POST' });
      if (response.status === 403) {
        setUser(null);
        setIsAuthenticated(false);
        if (!window.location.search.includes('error=access_denied')) {
          window.location.replace(`${window.location.origin}/?error=access_denied`);
        }
        return false;
      }
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await apiFetch('me');
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        return true;
      }
      if (response.status === 403) {
        setUser(null);
        setIsAuthenticated(false);
        if (!window.location.search.includes('error=access_denied')) {
          window.location.replace(`${window.location.origin}/?error=access_denied`);
        }
        return false;
      }
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } catch (error) {
      console.error('Errore verifica autenticazione:', error);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  }, []);

  const login = async () => {
    let authorizeBase = `${JEVIS_AUTH_URL}/api/v1/auth/authorize`;
    let callbackUrl = JEVIS_CALLBACK_URL;
    try {
      const res = await apiFetch('auth/config');
      if (res.ok) {
        const cfg = await res.json();
        if (cfg?.login_url) {
          authorizeBase = String(cfg.login_url).replace(/\/$/, '');
        }
        if (cfg?.callback_url) {
          callbackUrl = String(cfg.callback_url);
        }
      }
    } catch {
      // fallback ai default locali
    }
    const redirectBack = encodeURIComponent(callbackUrl);
    window.location.href = `${authorizeBase}?redirect_back=${redirectBack}`;
  };

  const logout = async () => {
    try {
      await apiFetch('auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Errore logout:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    checkAuth().finally(() => setLoading(false));
  }, [checkAuth]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = window.setInterval(async () => {
      const ok = await refreshSession();
      if (!ok) {
        await checkAuth();
      }
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [isAuthenticated, refreshSession, checkAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isAdmin: user?.role === 'admin',
        login,
        logout,
        loading,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
